import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { theme } from "../../../../../theme";
import { addPersonalInfoCustomField, deletePersonalInfoCustomField, getPersonalInfoCustomFields } from "../preboardingConfigurationApi";
import { useParams } from "react-router-dom";
import { useSnackbar } from "../../../../../Context/SnackbarContext";
import BasicInformationSection from "./BasicInformationSection";
import AddressDetailsSection from "./AddressDetailsSection";
import BankDetailsSection from "./BankDetailsSection";
import EmergencyContactSection from "./EmergencyContactSection";
import GovernmentIssuedIdSection from "./GovernmentIssuedIdSection";
import SchoolingDetailsSection from "./SchoolingDetailsSection";
import GraduationDetailsSection from "./GraduationDetailsSection";
import EmploymentDetailsSection from "./EmployeementDetailsSection";
import SkillsSection from "./SkillsSection";

export type CustomField = {
  customFieldId: number;
  name: string;
  type: string;
  mandatory: boolean;
  sectionId: number;
  sectionName: string;
  options?: string[];
  optionsRaw?: string;
  isDefault: boolean;
  placeholder: string;
};

export interface retrievedFieldsProps {
  CustomFieldId: number,
  FieldName: string;
  FieldType: string;
  IsMandatory: boolean;
  SectionId: number;
  SectionName: string;
}

export interface SectionProps {
  sectionName: string;
  sectionId: number;
}





const CandidateDetailsConfiguration: React.FC = () => {
  const [basicCustomFields, setBasicCustomFields] = useState<CustomField[]>([]);
  const { tenant } = useParams<{ tenant: string }>();
  const [addressCustomFields, setAddressCustomFields] = useState<CustomField[]>([]);
  const [bankCustomFields, setBankCustomFields] = useState<CustomField[]>([]);
  const [emergencyContactCustomFields, setEmergencyContactCustomFields] = useState<CustomField[]>([]);
  const [skillsCustomFields, setSkillsCustomFields] = useState<CustomField[]>([]);
  const [governmentIssuedIdCustomFields, setGovernmentIssuedIdCustomFields] = useState<CustomField[]>([]);
  const [schoolingCustomFields, setSchoolingCustomFields] = useState<CustomField[]>([]);
  const [graduationCustomFields, setGraduationCustomFields] = useState<CustomField[]>([]);
  const [employmentCustomFields, setEmploymentCustomFields] = useState<CustomField[]>([]);

  const [basicFieldErrors, setBasicFieldErrors] = useState<{ name?: string; type?: string }[]>([]);
  const [addressFieldErrors, setAddressFieldErrors] = useState<{ name?: string; type?: string }[]>([]);
  const [bankFieldErrors, setBankFieldErrors] = useState<{ name?: string; type?: string }[]>([]);
  const [emergencyContactFieldErrors, setEmergencyContactFieldErrors] = useState<{ name?: string; type?: string }[]>([]);
  const [skillsFieldErrors, setSkillsFieldErrors] = useState<{ name?: string; type?: string }[]>([]);
  const [governmentIssuedIdFieldErrors, setGovernmentIssuedIdFieldErrors] = useState<{ name?: string; type?: string }[]>([]);
  const [schoolingFieldErrors, setSchoolingFieldErrors] = useState<{ name?: string; type?: string }[]>([]);
  const [graduationFieldErrors, setGraduationFieldErrors] = useState<{ name?: string; type?: string }[]>([]);
  const [employmentFieldErrors, setEmploymentFieldErrors] = useState<{ name?: string; type?: string }[]>([]);

  const { showSnackbar } = useSnackbar();
  const [retrievedFields, setRetrievedFields] = useState<CustomField[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false); // State for delete confirmation dialog
  const [customFieldIdToDelete, setCustomFieldIdToDelete] = useState<number | null>(null);
  const [section, setSection] = useState<string>("");
  const [editedFieldIds, setEditedFieldIds] = useState<number[]>([]);
  const [sectionNames, setSectionNames] = useState<SectionProps[]>([])
  const [loading, setLoading] = useState<boolean>(true);


  const cancelDeleteCustomField = () => {
    setCustomFieldIdToDelete(null);
    setDeleteDialogOpen(false);
  }

  const fetchCustomFields = async () => {
    try {
      setLoading(true);
      const result = await getPersonalInfoCustomFields(tenant, false);
      if (result) {
        const transformedFields = result.Data.map((field: any) => {
          const isDropdown = field.FieldType === "dropdown";
          return {
            customFieldId: field.CustomFieldId,
            name: field.FieldName,
            type: field.FieldType,
            mandatory: field.IsMandatory,
            sectionName: field.SectionName,
            sectionId: field.SectionId,
            options: isDropdown ? field.Options || [] : undefined,
            optionsRaw: isDropdown && field.Options ? field.Options.join(", ") : undefined,
            isDefault: field.IsDefault || false
          };
        });
        // Exclude fields that are being edited
        const filtered = transformedFields.filter((field: any) => !editedFieldIds.includes(field.customFieldId));
        setRetrievedFields(filtered);
        setSectionNames(result.SectionNames.map((section: any) => ({
          sectionName: section.SectionName,
          sectionId: section.SectionId
        })));
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false);
    }

  };



  useEffect(() => {
    fetchCustomFields();
  }, [tenant])


  const handleAddCustomField = (
    section: string,
    setFields: React.Dispatch<React.SetStateAction<any[]>>
  ) => {
    setFields(prev => [
      ...prev,
      {
        customFieldId: 0,
        name: "",
        type: "text",
        mandatory: false,
        section,
      },
    ]);
  };


  const handleCustomFieldChange = (
    index: number,
    updatedField: CustomField,
    setFields: React.Dispatch<React.SetStateAction<CustomField[]>>,
    setErrors: React.Dispatch<React.SetStateAction<Record<string, any>[]>>
  ) => {
    setFields((prevFields) => {
      const updated = [...prevFields];
      updated[index] = updatedField;
      return updated;
    });

    setErrors((prevErrors) => {
      const updated = [...prevErrors];

      // Keep previous errors, only clear the one that matches the changed property
      const newErrors = { ...updated[index] };

      if (updatedField.name?.trim()) {
        delete newErrors.name; // clear only name error
      }
      if (updatedField.options?.length) {
        delete newErrors.options; // clear only options error
      }

      updated[index] = newErrors;
      return updated;
    });
  };



  const handleDeleteCustomField = (
    index: number,
    setFields: React.Dispatch<React.SetStateAction<CustomField[]>>,
    setErrors: React.Dispatch<React.SetStateAction<Record<string, any>[]>>
  ) => {
    setFields((prev) => prev.filter((_, i) => i !== index));
    setErrors((prev) => prev.filter((_, i) => i !== index));
  };


  const handleEditCustomField = (customFieldId: number) => {
    setRetrievedFields((prevRetrieved) => {
      const fieldToMove = prevRetrieved.find(f => f.customFieldId === customFieldId);
      if (!fieldToMove) return prevRetrieved;

      // Track this field as being edited
      setEditedFieldIds((prevIds) => [...prevIds, customFieldId]);

      // Remove from retrievedFields
      const updatedRetrieved = prevRetrieved.filter(f => f.customFieldId !== customFieldId);

      // Add to appropriate list based on section
      switch (fieldToMove.sectionName) {
        case sectionNames[0].sectionName:
          setBasicCustomFields((prev) => [...prev, fieldToMove]);
          setBasicFieldErrors((prev) => [...prev, {}]);
          break;
        case sectionNames[1].sectionName:
          setAddressCustomFields((prev) => [...prev, fieldToMove]);
          setAddressFieldErrors((prev) => [...prev, {}]);
          break;
        case sectionNames[2].sectionName:
          setEmergencyContactCustomFields((prev) => [...prev, fieldToMove]);
          setEmergencyContactFieldErrors((prev) => [...prev, {}]);
          break;
        case sectionNames[3].sectionName:
          setBankCustomFields((prev) => [...prev, fieldToMove]);
          setBankFieldErrors((prev) => [...prev, {}]);
          break;
        case sectionNames[4].sectionName:
          setGovernmentIssuedIdCustomFields((prev) => [...prev, fieldToMove]);
          setGovernmentIssuedIdFieldErrors((prev) => [...prev, {}]);
          break;
        case sectionNames[5].sectionName:
          setSchoolingCustomFields((prev) => [...prev, fieldToMove]);
          setSchoolingFieldErrors((prev) => [...prev, {}]);
          break;
        case sectionNames[6].sectionName:
          setGraduationCustomFields((prev) => [...prev, fieldToMove]);
          setGraduationFieldErrors((prev) => [...prev, {}]);
          break;
        case sectionNames[7].sectionName:
          setEmploymentCustomFields((prev) => [...prev, fieldToMove]);
          setEmploymentFieldErrors((prev) => [...prev, {}]);
          break;
        case sectionNames[8].sectionName:
          setSkillsCustomFields((prev) => [...prev, fieldToMove]);
          setSkillsFieldErrors((prev) => [...prev, {}]);
          break;
        default:
          console.warn("Unknown section:", fieldToMove.sectionName);
      }

      return updatedRetrieved;
    });
  };



  const validateField = (field: CustomField): { name?: string; type?: string; options?: string } => {
    const errors: { name?: string; type?: string; options?: string } = {};
    if (!field.name.trim()) {
      errors.name = "Field name is required.";
    } else if (field.name.length > 100) {
      errors.name = "Field name must be at most 100 characters.";
    }
    if (!field.type.trim()) errors.type = "Field type is required.";
    if (field.type === "dropdown") {
      if (!field.optionsRaw || !field.optionsRaw.trim()) {
        errors.options = "Dropdown options are required.";
      } else {
        // Validate at least one non-empty option
        const optionsArr = field.optionsRaw.split(",").map(opt => opt.trim()).filter(opt => opt);
        if (optionsArr.length === 0) {
          errors.options = "Please enter at least one valid option.";
        } else {
          // Check each option for max length
          const longOption = optionsArr.find(opt => opt.length > 100);
          if (longOption) {
            errors.options = "options must be at most 100 characters.";
          }
        }
      }
    }
    return errors;
  };

  const handleSaveCustomField = async (
    index: number,
    fieldArray: any[],
    setFieldArray: React.Dispatch<React.SetStateAction<any[]>>,
    fieldErrorsSetter: React.Dispatch<React.SetStateAction<any[]>>,
    sectionId: number
  ) => {
    const fieldToValidate = fieldArray[index];
    const error = validateField(fieldToValidate);

    fieldErrorsSetter((prevErrors) => {
      const newErrors = [...prevErrors];
      newErrors[index] = error;
      return newErrors;
    });

    if (Object.keys(error).length > 0) return;

    try {
      const payLoad = {
        CustomFieldId: fieldToValidate.customFieldId,
        FieldName: fieldToValidate.name,
        FieldType: fieldToValidate.type,
        Mandatory: fieldToValidate.mandatory,
        SectionId: sectionId,
        Options: fieldToValidate.options || fieldToValidate.optionsRaw?.split(",").map((opt: string) => opt.trim()).filter((opt: string) => opt) || []
      };

      const response = await addPersonalInfoCustomField(payLoad, tenant);
      if (response) {
        showSnackbar(response.Message, response.Status ? "success" : "error");
        if (response.Status) {
          setFieldArray((prev) => prev.filter((_, i) => i !== index));

          // ✅ Remove from edited list
          setEditedFieldIds((prev) =>
            prev.filter((id) => id !== fieldToValidate.customFieldId)
          );

          // ✅ Add manually to retrievedFields
          setRetrievedFields((prev) => {
            const updatedFields = [
              ...prev,
              {
                customFieldId: response.CustomFieldId,
                name: fieldToValidate.name,
                type: fieldToValidate.type,
                mandatory: fieldToValidate.mandatory,
                sectionId: sectionId,
                sectionName: sectionNames.find(s => s.sectionId === sectionId)?.sectionName || "",
                optionsRaw: fieldToValidate.optionsRaw || "",
                isDefault: fieldToValidate.isDefault || false,
                placeholder: fieldToValidate.placeholder || ""
              }
            ];

            // Sort by customFieldId in increasing order
            return updatedFields.sort((a, b) => a.customFieldId - b.customFieldId);
          });

          // fetchCustomFields(); // Or skip this if you added it manually
        }

      }
    } catch (error) {
      console.error(error);
      showSnackbar("Failed to add custom field", "error");
    }
  };




  const confirmDeleteCustomField = async () => {
    if (customFieldIdToDelete === null) return;

    try {
      const response = await deletePersonalInfoCustomField(customFieldIdToDelete, tenant);
      if (response) {
        showSnackbar(response.Message, response.Status ? "success" : "error");
        if (response.Status) {
          fetchCustomFields();
          if (section === sectionNames[0].sectionName) {
            setBasicCustomFields((prev) => prev.filter((field) => field.customFieldId !== customFieldIdToDelete));
          } else if (section === sectionNames[1].sectionName) {
            setAddressCustomFields((prev) => prev.filter((field) => field.customFieldId !== customFieldIdToDelete));
          } else if (section === sectionNames[2].sectionName) {
            setEmergencyContactCustomFields((prev) => prev.filter((field) => field.customFieldId !== customFieldIdToDelete));
          } else if (section === sectionNames[3].sectionName) {
            setBankCustomFields((prev) => prev.filter((field) => field.customFieldId !== customFieldIdToDelete));
          } else if (section === sectionNames[4].sectionName) {
            setGovernmentIssuedIdCustomFields((prev) => prev.filter((field) => field.customFieldId !== customFieldIdToDelete));
          } else if (section === sectionNames[5].sectionName) {
            setSchoolingCustomFields((prev) => prev.filter((field) => field.customFieldId !== customFieldIdToDelete));
          } else if (section === sectionNames[6].sectionName) {
            setGraduationCustomFields((prev) => prev.filter((field) => field.customFieldId !== customFieldIdToDelete));
          } else if (section === sectionNames[7].sectionName) {
            setEmploymentCustomFields((prev) => prev.filter((field) => field.customFieldId !== customFieldIdToDelete));
          } else if (section === sectionNames[8].sectionName) {
            setSkillsCustomFields((prev) => prev.filter((field) => field.customFieldId !== customFieldIdToDelete));
          }
        }
      }
    } catch (error) {
      console.log(error);
      showSnackbar("Failed to delete custom field", "error");
    } finally {
      setDeleteDialogOpen(false);
      setCustomFieldIdToDelete(null);
    }
  }

  // if(sectionDa)



  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        width="100%"
      >
        <CircularProgress />
      </Box>
    )
  }


  return (
    <Box mt={2}>
      <Grid container spacing={3}>
        {/* Left Main Section */}
        <Grid item >

          <Typography variant="h5">
            Personal Details
          </Typography>

          <Divider sx={{ mb: 2, mt: 2 }} />

          <BasicInformationSection
            retrievedFields={retrievedFields}
            basicCustomFields={basicCustomFields}
            basicFieldErrors={basicFieldErrors}
            handleEditCustomField={handleEditCustomField}
            setDeleteDialogOpen={setDeleteDialogOpen}
            setCustomFieldIdToDelete={setCustomFieldIdToDelete}
            handleAddCustomField={handleAddCustomField}
            setBasicCustomFields={setBasicCustomFields}
            handleCustomFieldChange={handleCustomFieldChange}
            handleDeleteCustomField={handleDeleteCustomField}
            handleSaveCustomField={handleSaveCustomField}
            setBasicFieldErrors={setBasicFieldErrors}
            setSection={setSection}
            sectionData={sectionNames[0]}
          />



          <AddressDetailsSection
            retrievedFields={retrievedFields}
            addressCustomFields={addressCustomFields}
            addressFieldErrors={addressFieldErrors}
            handleEditCustomField={handleEditCustomField}
            setDeleteDialogOpen={setDeleteDialogOpen}
            setCustomFieldIdToDelete={setCustomFieldIdToDelete}
            handleAddCustomField={handleAddCustomField}
            setAddressCustomFields={setAddressCustomFields}
            handleCustomFieldChange={handleCustomFieldChange}
            handleDeleteCustomField={handleDeleteCustomField}
            handleSaveCustomField={handleSaveCustomField}
            setAddressFieldErrors={setAddressFieldErrors}
            setSection={setSection}
            sectionData={sectionNames[1]}
          />


          <EmergencyContactSection
            retrievedFields={retrievedFields}
            emergencyContactCustomFields={emergencyContactCustomFields}
            emergencyContactFieldErrors={emergencyContactFieldErrors}
            handleEditCustomField={handleEditCustomField}
            setDeleteDialogOpen={setDeleteDialogOpen}
            setCustomFieldIdToDelete={setCustomFieldIdToDelete}
            handleAddCustomField={handleAddCustomField}
            setEmergencyContactCustomFields={setEmergencyContactCustomFields}
            handleCustomFieldChange={handleCustomFieldChange}
            handleDeleteCustomField={handleDeleteCustomField}
            handleSaveCustomField={handleSaveCustomField}
            setEmergencyContactFieldErrors={setEmergencyContactFieldErrors}
            setSection={setSection}
            sectionData={sectionNames[2]}

          />

          <SkillsSection
            retrievedFields={retrievedFields}
            skillsCustomFields={skillsCustomFields}
            skillsFieldErrors={skillsFieldErrors}
            handleEditCustomField={handleEditCustomField}
            setDeleteDialogOpen={setDeleteDialogOpen}
            setCustomFieldIdToDelete={setCustomFieldIdToDelete}
            handleAddCustomField={handleAddCustomField}
            setSkillsCustomFields={setSkillsCustomFields}
            handleCustomFieldChange={handleCustomFieldChange}
            handleDeleteCustomField={handleDeleteCustomField}
            handleSaveCustomField={handleSaveCustomField}
            setSkillsFieldErrors={setSkillsFieldErrors}
            setSection={setSection}
            sectionData={sectionNames.find(s => s.sectionName === "Skills") || sectionNames[8]}
          />

          <Typography variant="h5">
            Professional Details
          </Typography>

          <Divider sx={{ mb: 2, mt: 2 }} />

          <BankDetailsSection
            retrievedFields={retrievedFields}
            bankCustomFields={bankCustomFields}
            bankFieldErrors={bankFieldErrors}
            handleEditCustomField={handleEditCustomField}
            setDeleteDialogOpen={setDeleteDialogOpen}
            setCustomFieldIdToDelete={setCustomFieldIdToDelete}
            handleAddCustomField={handleAddCustomField}
            setBankCustomFields={setBankCustomFields}
            handleCustomFieldChange={handleCustomFieldChange}
            handleDeleteCustomField={handleDeleteCustomField}
            handleSaveCustomField={handleSaveCustomField}
            setBankFieldErrors={setBankFieldErrors}
            setSection={setSection}
            sectionData={sectionNames[3]}
          />

          <GovernmentIssuedIdSection
            retrievedFields={retrievedFields}
            governmentIssuedIdCustomFields={governmentIssuedIdCustomFields}
            governmentIssuedIdFieldErrors={governmentIssuedIdFieldErrors}
            handleEditCustomField={handleEditCustomField}
            setDeleteDialogOpen={setDeleteDialogOpen}
            setCustomFieldIdToDelete={setCustomFieldIdToDelete}
            handleAddCustomField={handleAddCustomField}
            setGovernmentIssuedIdCustomFields={setGovernmentIssuedIdCustomFields}
            handleCustomFieldChange={handleCustomFieldChange}
            handleDeleteCustomField={handleDeleteCustomField}
            handleSaveCustomField={handleSaveCustomField}
            setGovernmentIssuedIdFieldErrors={setGovernmentIssuedIdFieldErrors}
            setSection={setSection}
            sectionData={sectionNames[4]}
          />

          <SchoolingDetailsSection
            retrievedFields={retrievedFields}
            schoolingCustomFields={schoolingCustomFields}
            schoolingFieldErrors={schoolingFieldErrors}
            handleEditCustomField={handleEditCustomField}
            setDeleteDialogOpen={setDeleteDialogOpen}
            setCustomFieldIdToDelete={setCustomFieldIdToDelete}
            handleAddCustomField={handleAddCustomField}
            setSchoolingCustomFields={setSchoolingCustomFields}
            handleCustomFieldChange={handleCustomFieldChange}
            handleDeleteCustomField={handleDeleteCustomField}
            handleSaveCustomField={handleSaveCustomField}
            setSchoolingFieldErrors={setSchoolingFieldErrors}
            setSection={setSection}
            sectionData={sectionNames[5]}
          />

          <GraduationDetailsSection
            retrievedFields={retrievedFields}
            graduationCustomFields={graduationCustomFields}
            graduationFieldErrors={graduationFieldErrors}
            handleEditCustomField={handleEditCustomField}
            setDeleteDialogOpen={setDeleteDialogOpen}
            setCustomFieldIdToDelete={setCustomFieldIdToDelete}
            handleAddCustomField={handleAddCustomField}
            setGraduationCustomFields={setGraduationCustomFields}
            handleCustomFieldChange={handleCustomFieldChange}
            handleDeleteCustomField={handleDeleteCustomField}
            handleSaveCustomField={handleSaveCustomField}
            setGraduationFieldErrors={setGraduationFieldErrors}
            setSection={setSection}
            sectionData={sectionNames[6]}
          />

          <EmploymentDetailsSection
            retrievedFields={retrievedFields}
            employmentCustomFields={employmentCustomFields}
            employmentFieldErrors={employmentFieldErrors}
            handleEditCustomField={handleEditCustomField}
            setDeleteDialogOpen={setDeleteDialogOpen}
            setCustomFieldIdToDelete={setCustomFieldIdToDelete}
            handleAddCustomField={handleAddCustomField}
            setEmploymentCustomFields={setEmploymentCustomFields}
            handleCustomFieldChange={handleCustomFieldChange}
            handleDeleteCustomField={handleDeleteCustomField}
            handleSaveCustomField={handleSaveCustomField}
            setEmploymentFieldErrors={setEmploymentFieldErrors}
            setSection={setSection}
            sectionData={sectionNames[7]}
          />

        </Grid>

      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDeleteCustomField}
      >
        <DialogTitle>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this custom field? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteCustomField} sx={{ color: theme.palette.text.secondary }}>
            Cancel
          </Button>
          <Button onClick={confirmDeleteCustomField}
            variant="contained"
            sx={{
              bgcolor: theme.palette.error.main,
              '&:hover': {
                bgcolor: theme.palette.error.dark,
              },
            }}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CandidateDetailsConfiguration;